import React from 'react';
import cn from 'classnames';
import { Button } from 'antd';
import { CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
// Types
import { GetCampaignOffers_getCampaignOffers_entities_deliverablesV2 } from 'api/campaignV2/types/GetCampaignOffers';
import { CbDeliverableV2Status } from 'api/graphql-global-types';
// Helpers
import {
  formatAppearanceDate,
  getAppearanceConfirmationItem,
  getAppearanceDateValue,
} from 'helpers/deliverables';
// UI
import Text from 'uiShared/Text/Text';
// Components
import MarkAppearanceCompleteButton from './components/MarkAppearanceCompleteButton/MarkAppearanceCompleteButton';
// Styles
import styles from './DeliverableAppearanceCard.module.scss';

type DeliverableAppearanceCardProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  offerId: string;
  campaignId: string;
  className?: string;
  refetch?: () => void;
  hasPendingRequest?: (deliverableId: string) => boolean;
  onRequestExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
  onReviewExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
};

const DeliverableAppearanceCard: React.FC<DeliverableAppearanceCardProps> = ({
  deliverable,
  offerId,
  campaignId,
  className,
  refetch,
  hasPendingRequest,
  onRequestExtension,
  onReviewExtension,
}) => {
  const task =
    deliverable?.task?.__typename === 'CampaignAppearanceTask'
      ? deliverable.task
      : null;
  const title = deliverable?.task?.title;
  const description = deliverable?.task?.description;
  const location = task?.location;
  const appearanceDate = getAppearanceDateValue(deliverable);
  const confirmationItem = getAppearanceConfirmationItem(deliverable);

  const getStatusLabel = () => {
    if (!deliverable?.status) return null;
    return deliverable.status.replace(/_/g, ' ').toLowerCase();
  };

  // Appearance is event-based: the extension moves the scheduled date. The
  // backend rejects requests once the appearance is approved/completed.
  const isPending = hasPendingRequest?.(deliverable.id) ?? false;
  const showExtensionButton =
    Boolean(onRequestExtension && onReviewExtension && hasPendingRequest) &&
    deliverable.status !== CbDeliverableV2Status.ON_BRAND_REVIEW &&
    deliverable.status !== CbDeliverableV2Status.APPROVED;

  return (
    <div className={cn(styles.card, className)}>
      <div className={styles.labelSection}>
        <div className={styles.deliverableLabel}>
          Deliverable {(deliverable?.task?.orderIdx ?? 0) + 1}: {title} -
          Appearance
        </div>
        {getStatusLabel() && (
          <div className={styles.status}>{getStatusLabel()}</div>
        )}
      </div>

      {description && (
        <Text className={styles.description} color="black">
          {description}
        </Text>
      )}

      {location && (
        <div className={styles.dateRow}>
          <EnvironmentOutlined className={styles.icon} />
          <Text className={styles.dateLabel}>Location:</Text>
          <Text className={styles.dateValue}>{location}</Text>
        </div>
      )}

      <div className={styles.dateRow}>
        <CalendarOutlined className={styles.icon} />
        <Text className={styles.dateLabel}>Appearance Date:</Text>
        <Text className={styles.dateValue}>
          {formatAppearanceDate(appearanceDate)}
        </Text>
      </div>

      {showExtensionButton && (
        <div className={styles.extensionButton}>
          {isPending ? (
            <Button
              type="primary"
              block
              onClick={() => onReviewExtension?.(deliverable)}
            >
              Review Extension
            </Button>
          ) : (
            <Button block onClick={() => onRequestExtension?.(deliverable)}>
              Request Extension
            </Button>
          )}
        </div>
      )}

      <MarkAppearanceCompleteButton
        offerId={offerId}
        campaignId={campaignId}
        appearanceDate={appearanceDate}
        confirmationItem={confirmationItem}
        refetch={refetch}
      />
    </div>
  );
};

export default DeliverableAppearanceCard;
